import { IconButton, IconProps, makeStyles, styled, Tab, Tabs, withStyles } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useIO, useLinked, useModel } from "@type-r/react";
import { InblockIcon } from "common/invoice-aging/iconography";
import { Button, CollapsableSection, Column, div, Flex, Loading } from "lqx-react";
import React from "react";
import { CreditNote } from "../api/creditNote";
import { ExternalSystemRequest, ExternalSystemType } from "../api/externalSystemRequest";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { ActivityLog } from "../invoices/invoice-activity-logs-tab";

export const CreditNoteDetailsPane = ({ creditNote, close, creditNoteId, $selectedTab }: { creditNote: CreditNote, close, creditNoteId, $selectedTab }) => {
    const classes = useCreditNoteDetailsPaneStyles();
    const $doSendToExternalSystem = useLinked(false);
    const $setSendToExternalType = useLinked(ExternalSystemType.none);
    const externalSystemRequest = useModel(ExternalSystemRequest);
    const inblockIconClasses = useInblockIconStyles();
    const inblockedIconClases = useInblockedIconStyles();

    const isSendToExternalSystemReady = useIO(
        async () =>
            $doSendToExternalSystem.isTruthy &&
            $setSendToExternalType.value != ExternalSystemType.none &&
            await sendToExternalSystem($setSendToExternalType.value),
        [$doSendToExternalSystem.value]
    );

    const sendToExternalSystem = async (externalSystem: string) => {
        try {
            if (!creditNote.uid) return;

            externalSystemRequest.set({
                to: externalSystem,
                assetType: "CreditNote",
                assetUids: [creditNote.uid]
            });
            await externalSystemRequest.save();
        }
        finally {
            $doSendToExternalSystem.set(false);
        }
    };

    const SendTo = () => {
        return (
            <SendToContent>
                Choose the workflow where you want to transmit your digitized Asset:
                <LiquidxIcon />
                <Flex row>
                    <SendToButton
                        onClick={() => $setSendToExternalType.set(ExternalSystemType.ar)}>
                        AR
                    </SendToButton>
                    <SendToButton
                        onClick={() => $setSendToExternalType.set(ExternalSystemType.scf)}>
                        SCF
                    </SendToButton>
                </Flex>
                <InblockIcon src="/assets/images/inblock.png" classes={inblockIconClasses} />
                <Flex row>
                    <SendToButton
                        color="primary"
                        onClick={() => $setSendToExternalType.set(ExternalSystemType.inblock)}>
                        Order Management
                    </SendToButton>
                    <SendToButton
                        onClick={() => $setSendToExternalType.set(ExternalSystemType.inblock)}>
                        Invoice Management
                    </SendToButton>
                </Flex>
                <Flex row>
                    <ConfirmButton onClick={() => $doSendToExternalSystem.set(true)}>Confirm</ConfirmButton>
                </Flex>
            </SendToContent>
        );
    }

    return isSendToExternalSystemReady ?
        <>
            <Container>
                <Header>
                    <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => close()}>
                        <CloseIcon />
                    </IconButton>
                    <Flex alignCenter>
                        {creditNote.isDigitized ? <>
                            <InblockIcon src="/assets/images/inblock_logo.png" classes={inblockedIconClases} />
                            <StatusWithoutMargin>{creditNote.state.name}</StatusWithoutMargin> </>
                            : <Status>{creditNote.state.name}</Status>}
                    </Flex>
                    <HeaderTitle><>Credit Note {creditNote.number ? creditNote.number : ''}</></HeaderTitle>
                </Header>
                <CreditNoteTabs value={$selectedTab.value}
                    variant="scrollable"
                    scrollButtons="auto"
                    onChange={(evt, newValue) => $selectedTab.set(newValue)} >
                    <CreditNoteTab value="details" label=" Details" />
                    <CreditNoteTab value="activityLog" label="Activity Log" />
                    {/* {creditNote.isDigitized && <CreditNoteTab value="sendTo" label=" Send To" />} */}
                </CreditNoteTabs>
                <CreditNoteTabContent>
                    {$selectedTab.value === 'details' && <Details creditNote={creditNote} creditNoteId={creditNoteId} />}
                    {$selectedTab.value === 'activityLog' && <ActivityLog assetId={creditNoteId} />}
                    {/* {$selectedTab.value === 'sendTo' && <SendTo />} */}
                </CreditNoteTabContent>
            </Container>
        </> : <Loading />
};

const Details = ({ creditNote, creditNoteId }: { creditNote: CreditNote, creditNoteId }) => {
    const classes = useCreditNoteDetailsPaneStyles();
    const sectionStyles = SectionStyles();

    return (<>
        <CollapsableSection title="General" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                <CopyRow label="Supplier Name" value={creditNote.supplier.name} />
                <CopyRow label="Buyer name" value={creditNote.buyer.name} />
                <CopyRow label="Credit Note Reason" value={creditNote.reason} />
                <CopyRow label="Credit Note Amount" currency value={creditNote.amount} />
                <CopyRow label="Credit Note Outstanding Amount" currency value={creditNote.outstandingAmount} />
                <CopyRow label="Currency" value={creditNote.currency} />
                <CopyRow label="Issuance Date" date={!!creditNote.issuanceDate} value={creditNote.issuanceDate} />
                <CopyRow label="Credit Note Number" value={creditNote.number} />
                <CopyRow label="Payment Terms" value={creditNote.paymentTerms} />
                <CopyRow label="Expiration Date" date={!!creditNote.expirationDate} value={creditNote.expirationDate} />
                <CopyRow label="Sales Order Number" value={creditNote.detail.saleOrder.number} />
                <CopyRow label="Purchase Order Number" value={creditNote.detail.purchaseOrder.number} />
                <CopyRow label="Invoice Number" value={creditNote.detail.invoice.number} />
                <CopyRow label="Asset ID" value={creditNoteId} />
                <CopyRow label="Asset UID" value={creditNote.uid} />
                <CopyRow label="Import Job ID" value={creditNote.importJobId} hyperlinkHref={`/import-jobs/?importJobId=${creditNote.importJobId}`} />
                <CopyRow label="Created Date" date={!!creditNote.createdOn} value={creditNote.createdOn} />
                <CopyRow label="Updated Date" date={!!creditNote.updatedOn} value={creditNote.updatedOn} />
            </Column>
        </CollapsableSection>
    </>);
}

const Container = div({
    backgroundColor: 'white',
    padding: '0px',
    marginTop: '26px',
    height: '100%'
});

const Header = div({
    display: 'contents',
    width: '100%',
    height: '100%',
    border: '1px solid #E2F0F7',
    paddingTop: 0
});

const Status = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    marginLeft: '21px',
    fontWeight: 500,
    lineHeight: '15px',
});

const StatusWithoutMargin = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const CreditNoteTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const CreditNoteTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const CreditNoteTabContent = div({
    border: '1px solid #E2F0F7',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
});

const useCreditNoteDetailsPaneStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    },
    textAlignRight: {
        textAlign: 'right',
        paddingRight: '20px',
    }
})

const LiquidxIcon = ({ ...rest }: Partial<IconProps>) => {
    const classes = useLiquidxIconStyles(rest);
    return <img className={classes.root} src="/assets/images/liquidx.png" />
}

const useLiquidxIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px',
        marginLeft: '21px',
    }
});

const useInblockIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const SendToButton = styled(Button)({
    minWidth: '0px',
    marginTop: '20px',
    fontSize: '12px'
});

const SendToContent = div({
    padding: '30px'
});

const SectionStyles = makeStyles({
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    },
})

const ConfirmButton = styled(Button)({
    marginBottom: '10px',
    backgroundColor: '#4197d6',
    border: '0px',
    color: 'white',
    borderRadius: '10px',
    height: '25px',
    marginLeft: 'auto',
    marginTop: '20px',
    '&:hover': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:active': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:focus': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    }
});
